import type { AuthUser } from './types';

const STORAGE_TOKEN = 'token';
const STORAGE_REFRESH = 'refresh_token';
const STORAGE_USER = 'user';
const STORAGE_EXPIRES = 'token_expires_at';

interface AppState {
    token: string | null;
    refreshToken: string | null;
    // Время истечения access-токена (мс, Unix epoch) — нужно для упреждающего обновления.
    expiresAt: number | null;
    user: AuthUser | null;
    currentPlayerId: number | null;

    reviewMode: {
        active: boolean;
        queue: { id: number; name: string }[];   // очередь игроков для оценки
        index: number;                            // текущий индекс
    } | null;

    onBackToList: (() => void) | null;
}

export const state: AppState = {
    token: localStorage.getItem(STORAGE_TOKEN),
    refreshToken: localStorage.getItem(STORAGE_REFRESH),
    expiresAt: readExpires(),
    user: readUser(),
    currentPlayerId: null,
    reviewMode: null,

    onBackToList: null,
};

/** Снимок сессии из localStorage (для синхронизации между вкладками). */
export interface StoredSession {
    token: string | null;
    refreshToken: string | null;
    expiresAt: number | null;
    user: AuthUser | null;
}

function readUser(): AuthUser | null {
    const raw = localStorage.getItem(STORAGE_USER);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (
            parsed &&
            typeof parsed === 'object' &&
            'id' in parsed &&
            'email' in parsed
        ) {
            return parsed as AuthUser;
        }
        return null;
    } catch {
        return null;
    }
}

function readExpires(): number | null {
    const raw = localStorage.getItem(STORAGE_EXPIRES);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Достаёт exp (мс, Unix epoch) из JWT без проверки подписи.
 * Нужно только чтобы знать, когда access-токен протухнет.
 */
export function jwtExpiryMs(token: string): number | null {
    const payload = token.split('.')[1];
    if (!payload) return null;
    try {
        const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        const json = atob(pad ? b64 + '='.repeat(4 - pad) : b64);
        const exp = (JSON.parse(json) as { exp?: number }).exp;
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
}

export function readStoredSession(): StoredSession {
    return {
        token: localStorage.getItem(STORAGE_TOKEN),
        refreshToken: localStorage.getItem(STORAGE_REFRESH),
        expiresAt: readExpires(),
        user: readUser(),
    };
}

/**
 * Перечитывает сессию из localStorage в память и уведомляет UI.
 * Используется при событии `storage`: другая вкладка обновила токены/вошла/вышла.
 */
export function syncSessionFromStorage(): void {
    const stored = readStoredSession();
    state.token = stored.token;
    state.refreshToken = stored.refreshToken;
    state.expiresAt = stored.expiresAt;
    state.user = stored.user;
    window.dispatchEvent(new Event('session:changed'));
}

/**
 * Сохраняет сессию. exp access-токена вычисляем сами (из JWT), если не передан явно.
 * expiresAtMs — время истечения в миллисекундах Unix epoch.
 */
export function saveSession(
    user: AuthUser,
    accessToken: string,
    refreshToken?: string,
    expiresAtMs?: number | null
): void {
    state.user = user;
    state.token = accessToken;

    const exp =
        typeof expiresAtMs === 'number' && Number.isFinite(expiresAtMs)
            ? expiresAtMs
            : jwtExpiryMs(accessToken);
    state.expiresAt = exp;
    localStorage.setItem(STORAGE_USER, JSON.stringify(user));
    localStorage.setItem(STORAGE_TOKEN, accessToken);
    if (exp === null) localStorage.removeItem(STORAGE_EXPIRES);
    else localStorage.setItem(STORAGE_EXPIRES, String(exp));

    if (refreshToken) {
        state.refreshToken = refreshToken;
        localStorage.setItem(STORAGE_REFRESH, refreshToken);
    }

    window.dispatchEvent(new Event('session:changed'));
}

export function clearSession(): void {
    state.user = null;
    state.token = null;
    state.refreshToken = null;
    state.expiresAt = null;
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_REFRESH);
    localStorage.removeItem(STORAGE_EXPIRES);

    window.dispatchEvent(new Event('session:changed'));
}

// Синхронизация сессии между вкладками: storage-событие приходит только в других вкладках,
// поэтому при входе/выходе/обновлении токена в одной вкладке остальные подхватывают актуальное.
window.addEventListener('storage', (e) => {
    if (
        e.key === null ||
        e.key === STORAGE_TOKEN ||
        e.key === STORAGE_REFRESH ||
        e.key === STORAGE_USER ||
        e.key === STORAGE_EXPIRES
    ) {
        syncSessionFromStorage();
    }
});
